import { ParseKeys } from 'i18next'
import { myPalette } from '~/shared/lib/theme'
import { CountryEnum } from './countryEnum'
import {
  TransmissionEnum,
  FuelTypeEnum,
  RentalContractWorkScheduleEnum,
  RentalContractTypeEnum,
  TransactionMoveFundsTypeEnum,
  TransactionPaymentTypeEnum,
  TransactionBalanceTypeEnum,
  WeekDaysEnum,
  AppealsStatusEnum,
  CarUnderRepairSubStatusEnum,
  CarAtWorkSubStatusEnum,
  DayEnum,
  CarPropertyTypeEnum,
  TimezoneCode,
  LocaleEnum,
  InfoSourceEnum,
  RentalContractQuantityOfHoursEnum,
  ParkingCardVehicleTypeEnum,
} from './enums'

export const AUTH_TOKEN_STORAGE_KEY = 'auth_token'
export const USER_PERMISSIONS_STORAGE_KEY = 'user_permissions'
export const USER_TAXIPARK_STORAGE_KEY = 'user_taxipark'
export const USER_REGIONS_STORAGE_KEY = 'user_regions'
export const USER_LOCALE_STORAGE_KEY = 'user_locale'
export const USER_COUNTRY_STORAGE_KEY = 'user_country'
export const USER_DEPOSIT_BALANCE_ENABLED_STORAGE_KEY =
  'user_deposit_balance_enabled'

export const FILTER_WITHOUT_EMPTY_ENTITIES = '_'
export const CAR_VIN_LENGTH = 17
export const MIN_COLUMN_WIDTH = 90

export const DRIVER_KBM_WARNING_COEF = 1.17
export const DRIVER_KBM_ERROR_COEF = 2

export type Option = { id: string; label: string }
export type OptionEnum<T> = { id: T; label: string | ParseKeys }

export const countries: OptionEnum<CountryEnum>[] = Object.keys(
  CountryEnum,
).map((value) => {
  return {
    label: `country.${value}`,
    id: value as CountryEnum,
  }
})

export const transmissionEnumLabels: Record<TransmissionEnum, ParseKeys> = {
  [TransmissionEnum.AUTO]: 'transmission.automatic transmission',
  [TransmissionEnum.MANUAL]: 'transmission.manual transmission',
}
export const transmissionEnumOptions: OptionEnum<TransmissionEnum>[] =
  Object.keys(TransmissionEnum).map((value) => {
    const label = transmissionEnumLabels[value as keyof typeof TransmissionEnum]
    return {
      label,
      id: value as TransmissionEnum,
    }
  })

export const fuelTypeEnumLabels: Record<FuelTypeEnum, ParseKeys> = {
  [FuelTypeEnum.AI92]: 'fuel type.ai 92',
  [FuelTypeEnum.AI95]: 'fuel type.ai 95',
  [FuelTypeEnum.AI98]: 'fuel type.ai 98',
  [FuelTypeEnum.AI100]: 'fuel type.ai 100',
  [FuelTypeEnum.DIESEL]: 'fuel type.diesel',
  [FuelTypeEnum.PROPANE]: 'fuel type.propane',
  [FuelTypeEnum.METHANE]: 'fuel type.methane',
  [FuelTypeEnum.ELECTRICITY]: 'fuel type.electricity',
}
export const fuelTypeEnumOptions: OptionEnum<FuelTypeEnum>[] = Object.keys(
  FuelTypeEnum,
).map((value) => {
  const label = fuelTypeEnumLabels[value as keyof typeof FuelTypeEnum]
  return {
    label,
    id: value as FuelTypeEnum,
  }
})

export const parkingCardVehicleTypeEnumLabels: Record<
  ParkingCardVehicleTypeEnum,
  ParseKeys
> = {
  [ParkingCardVehicleTypeEnum.VTC]: 'parking card vehicle type.vtc',
}
export const parkingCardVehicleTypeEnumOptions: OptionEnum<ParkingCardVehicleTypeEnum>[] =
  Object.keys(ParkingCardVehicleTypeEnum).map((value) => {
    const label =
      parkingCardVehicleTypeEnumLabels[
        value as keyof typeof ParkingCardVehicleTypeEnum
      ]
    return {
      label,
      id: value as ParkingCardVehicleTypeEnum,
    }
  })

export const carUnderRepairSubStatusEnumLabels: Record<
  CarUnderRepairSubStatusEnum,
  ParseKeys
> = {
  // [CarUnderRepairSubStatusEnum.ACCIDENT]: 'car under repair sub status.accident',
  // [CarUnderRepairSubStatusEnum.BODY_REPAIR]: 'car under repair sub status.body repair',
  [CarUnderRepairSubStatusEnum.METALWORK_REPAIR]:
    'car under repair sub status.mechanical repair',
  [CarUnderRepairSubStatusEnum.VEHICLE_ON_THE_PENALTY_AREA]:
    'car under repair sub status.vehicle in impound lot',
  [CarUnderRepairSubStatusEnum.HEAVY_REPAIR]:
    'car under repair sub status.major repair',
  [CarUnderRepairSubStatusEnum.LOSS_OF_DOCUMENTS]:
    'car under repair sub status.lost documents',
  [CarUnderRepairSubStatusEnum.ABSENCE_OF_LICENSE]:
    'car under repair sub status.absence of license',
  [CarUnderRepairSubStatusEnum.EXPIRED_DOCUMENTS]:
    'car under repair sub status.expired documents',
  [CarUnderRepairSubStatusEnum.LOSS_OF_KEYS]:
    'car under repair sub status.lost key',
  [CarUnderRepairSubStatusEnum.HOUSEHOLD_WORK]:
    'car under repair sub status.utility work',
  [CarUnderRepairSubStatusEnum.GUARANTEE]:
    'car under repair sub status.warranty',
  [CarUnderRepairSubStatusEnum.DETAILING]:
    'car under repair sub status.detailing',
  [CarUnderRepairSubStatusEnum.BODY_REPAIR_LIGHT]:
    'car under repair sub status.light body repair',
  [CarUnderRepairSubStatusEnum.BODY_REPAIR_MEDIUM]:
    'car under repair sub status.medium body repair',
  [CarUnderRepairSubStatusEnum.BODY_REPAIR_HEAVY]:
    'car under repair sub status.heavy body repair',
  [CarUnderRepairSubStatusEnum.VEHICLE_PREPARATION]:
    'car under repair sub status.vehicle inspection',
  [CarUnderRepairSubStatusEnum.AGGREGATE]:
    'car under repair sub status.aggregate',
  [CarUnderRepairSubStatusEnum.TELEMATICS]:
    'car under repair sub status.telematics',
}

export const carUnderRepairSubStatusEnumOptions: OptionEnum<CarUnderRepairSubStatusEnum>[] =
  Object.keys(CarUnderRepairSubStatusEnum).map((value) => {
    const label =
      carUnderRepairSubStatusEnumLabels[
        value as keyof typeof CarUnderRepairSubStatusEnum
      ]
    return {
      label,
      id: value as CarUnderRepairSubStatusEnum,
    }
  })

export const carAtWorkSubStatusEnumLabels: Record<
  CarAtWorkSubStatusEnum,
  ParseKeys
> = {
  [CarAtWorkSubStatusEnum.CONFISCATION]: 'car at work sub status.seizure',
  [CarAtWorkSubStatusEnum.BREAKING]: 'car at work sub status.breakdown',
  [CarAtWorkSubStatusEnum.TAXISHARING]: 'car at work sub status.taxi sharing',
  [CarAtWorkSubStatusEnum.DETAILING]: 'car at work sub status.detailing',
  [CarAtWorkSubStatusEnum.EMPLOYEE]: 'car at work sub status.employee',
  [CarAtWorkSubStatusEnum.TRANSFER]: 'car at work sub status.relocation',
  [CarAtWorkSubStatusEnum.DOCUMENT_INVALID]:
    'car at work sub status.document invalid',
  [CarAtWorkSubStatusEnum.SALARY_CLIENT]:
    'car at work sub status.payroll client',
}

export const carAtWorkSubStatusEnumOptions: OptionEnum<CarAtWorkSubStatusEnum>[] =
  Object.keys(CarAtWorkSubStatusEnum).map((value) => {
    const label =
      carAtWorkSubStatusEnumLabels[value as keyof typeof CarAtWorkSubStatusEnum]

    return {
      label,
      id: value as CarAtWorkSubStatusEnum,
    }
  })

export const rentalContractWorkScheduleEnumOptions: Option[] = Object.keys(
  RentalContractWorkScheduleEnum,
).map((value) => {
  const label =
    RentalContractWorkScheduleEnum[
      value as keyof typeof RentalContractWorkScheduleEnum
    ]
  return {
    label,
    id: label,
  }
})

export const rentalContractTypeEnumLabels: Record<
  RentalContractTypeEnum,
  ParseKeys
> = {
  [RentalContractTypeEnum.RENT]: 'rental contract type.rent',
  [RentalContractTypeEnum.FREE]: 'rental contract type.free',
}

export const rentalContractTypeEnumOptions: OptionEnum<RentalContractTypeEnum>[] =
  Object.keys(rentalContractTypeEnumLabels).map((value) => {
    const label =
      rentalContractTypeEnumLabels[value as keyof typeof RentalContractTypeEnum]
    return {
      label,
      id: value as RentalContractTypeEnum,
    }
  })

export const transactionMoveFundsTypeEnumLabels: Record<
  TransactionMoveFundsTypeEnum,
  ParseKeys
> = {
  [TransactionMoveFundsTypeEnum.COMING]: 'transaction move funds type.income',
  [TransactionMoveFundsTypeEnum.SPENDING]:
    'transaction move funds type.expense',
}
export const transactionMoveFundsTypeEnumOptions: OptionEnum<TransactionMoveFundsTypeEnum>[] =
  Object.keys(transactionMoveFundsTypeEnumLabels).map((value) => {
    const label =
      transactionMoveFundsTypeEnumLabels[
        value as keyof typeof TransactionMoveFundsTypeEnum
      ]
    return {
      label,
      id: value as TransactionMoveFundsTypeEnum,
    }
  })

export const transactionPaymentTypeEnumLabels: Record<
  TransactionPaymentTypeEnum,
  ParseKeys
> = {
  [TransactionPaymentTypeEnum.CASH]: 'transaction payment type.cash payment',
  [TransactionPaymentTypeEnum.NON_CASH]:
    'transaction payment type.non-cash payment',
}

export const transactionPaymentTypeEnumOptions: OptionEnum<TransactionPaymentTypeEnum>[] =
  Object.keys(TransactionPaymentTypeEnum).map((value) => {
    const label =
      transactionPaymentTypeEnumLabels[
        value as keyof typeof TransactionPaymentTypeEnum
      ]
    return {
      label,
      id: value as TransactionPaymentTypeEnum,
    }
  })

export const transactionBalanceTypeEnumLabels: Record<
  TransactionBalanceTypeEnum,
  ParseKeys
> = {
  [TransactionBalanceTypeEnum.MAIN]: 'transaction balance type.current balance',
  [TransactionBalanceTypeEnum.BY_DEPT]:
    'transaction balance type.credit balance',
  [TransactionBalanceTypeEnum.BY_BONUS]:
    'transaction balance type.bonus balance',
  [TransactionBalanceTypeEnum.BY_DEPOSIT]:
    'transaction balance type.deposit balance',
}

export const transactionBalanceTypeEnumOptions: OptionEnum<TransactionBalanceTypeEnum>[] =
  Object.keys(TransactionBalanceTypeEnum).map((value) => {
    const label =
      transactionBalanceTypeEnumLabels[
        value as keyof typeof TransactionBalanceTypeEnum
      ]
    return {
      label,
      id: value as TransactionBalanceTypeEnum,
    }
  })

export const weekDaysEnumLabels: Record<WeekDaysEnum, ParseKeys> = {
  [WeekDaysEnum.MONDAY]: 'days.monday',
  [WeekDaysEnum.TUESDAY]: 'days.tuesday',
  [WeekDaysEnum.WEDNESDAY]: 'days.wednesday',
  [WeekDaysEnum.THURSDAY]: 'days.thursday',
  [WeekDaysEnum.FRIDAY]: 'days.friday',
  [WeekDaysEnum.SATURDAY]: 'days.saturday',
  [WeekDaysEnum.SUNDAY]: 'days.sunday',
}

export type WeekDayType = {
  id: string
  label: ParseKeys
  gender: 'male' | 'female' | 'neuter'
}
export const maleDays = [0, 1, 3]
export const femaleDays = [2, 4, 5]

export const weekDaysEnumOptions: WeekDayType[] = Object.keys(WeekDaysEnum).map(
  (value, index) => {
    const label = weekDaysEnumLabels[value as keyof typeof WeekDaysEnum]
    const gender = maleDays.includes(index)
      ? 'male'
      : femaleDays.includes(index)
      ? 'female'
      : 'neuter'
    return {
      id: String(index + 1),
      label,
      gender,
    }
  },
)

export const paymentProcedureEnumLabels: Record<
  RentalContractWorkScheduleEnum,
  ParseKeys
> = {
  [RentalContractWorkScheduleEnum.FULL_WEEK]: 'payment procedure.daily',
  [RentalContractWorkScheduleEnum.SIX_THROUGH_ONE]:
    'payment procedure.daily except weekend',
}

export const appealsStatusEnumLabels: Record<AppealsStatusEnum, ParseKeys> = {
  [AppealsStatusEnum.NEW]: 'appeals status.new',
  [AppealsStatusEnum.PROCESSING]: 'appeals status.in progress',
  [AppealsStatusEnum.SOLVED]: 'appeals status.resolved',
  [AppealsStatusEnum.REJECTED]: 'appeals status.rejected',
}

export const AppealsStatusEnumOptions: OptionEnum<AppealsStatusEnum>[] =
  Object.keys(AppealsStatusEnum).map((value) => {
    const label =
      appealsStatusEnumLabels[value as keyof typeof AppealsStatusEnum]
    return {
      label,
      id: value as AppealsStatusEnum,
    }
  })

export const daysEnumLabels: Record<DayEnum, ParseKeys> = {
  [DayEnum.Monday]: 'days.monday',
  [DayEnum.Tuesday]: 'days.tuesday',
  [DayEnum.Wednesday]: 'days.wednesday',
  [DayEnum.Thursday]: 'days.thursday',
  [DayEnum.Friday]: 'days.friday',
  [DayEnum.Saturday]: 'days.saturday',
  [DayEnum.Sunday]: 'days.sunday',
}

export const daysEnumOptions: OptionEnum<DayEnum>[] = Object.keys(DayEnum).map(
  (value) => {
    const label = daysEnumLabels[value as keyof typeof DayEnum]
    return {
      label,
      id: value as DayEnum,
    }
  },
)

export const rentalContractQuantityOfHoursLabels: Record<
  RentalContractQuantityOfHoursEnum,
  ParseKeys
> = {
  [RentalContractQuantityOfHoursEnum.ONE_WRITE_OFF]: 'form.every day',
  [RentalContractQuantityOfHoursEnum.TWO_WRITE_OFFS]: 'form.every 12 hours',
  [RentalContractQuantityOfHoursEnum.TWELVE_WRITE_OFFS]: 'form.every 2 hours',
}

export const rentalContractQuantityOfHoursEnumOptions = [
  RentalContractQuantityOfHoursEnum.ONE_WRITE_OFF,
  RentalContractQuantityOfHoursEnum.TWO_WRITE_OFFS,
  RentalContractQuantityOfHoursEnum.TWELVE_WRITE_OFFS,
].map((value) => ({
  id: String(value),
  label: rentalContractQuantityOfHoursLabels[value],
}))

export const carPropertyTypeEnumLabels: Record<CarPropertyTypeEnum, ParseKeys> =
  {
    [CarPropertyTypeEnum.LEASING]: 'car property type.leasing',
    [CarPropertyTypeEnum.LEASEBACK]: 'car property type.leaseback',
    [CarPropertyTypeEnum.PROPERTY]: 'car property type.ownership',
  }

export const carPropertyTypeEnumOptions: OptionEnum<CarPropertyTypeEnum>[] =
  Object.keys(CarPropertyTypeEnum).map((value) => {
    const label =
      carPropertyTypeEnumLabels[value as keyof typeof CarPropertyTypeEnum]
    return {
      label,
      id: value as CarPropertyTypeEnum,
    }
  })

export const timezoneCodeLabels: Record<TimezoneCode, ParseKeys> = {
  [TimezoneCode.AFRICA]: 'timezone code.africa utc+0',
  [TimezoneCode.KALININGRAD]: 'timezone code.kaliningrad utc+2',
  [TimezoneCode.MOSCOW]: 'timezone code.moscow utc+3',
  [TimezoneCode.SAMARA]: 'timezone code.samara utc+4',
  [TimezoneCode.YEKATERINBURG]: 'timezone code.yekaterinburg utc+5',
  [TimezoneCode.OMSK]: 'timezone code.omsk utc+6',
  [TimezoneCode.KRASNOYARSK]: 'timezone code.krasnoyarsk utc+7',
  [TimezoneCode.IRKUTSK]: 'timezone code.irkutsk utc+8',
  [TimezoneCode.YAKUTSK]: 'timezone code.yakutsk utc+9',
  [TimezoneCode.VLADIVOSTOK]: 'timezone code.vladivostok utc+10',
  [TimezoneCode.MAGADAN]: 'timezone code.magadan utc+11',
  [TimezoneCode.KAMCHATKA]: 'timezone code.kamchatka utc+12',
}

export const timezoneCodeOptions: OptionEnum<TimezoneCode>[] = Object.values(
  TimezoneCode,
).map((value) => ({
  label: timezoneCodeLabels[value as TimezoneCode],
  id: value as TimezoneCode,
}))

export const localeEnumLabels: Record<LocaleEnum, ParseKeys> = {
  [LocaleEnum.ru]: 'locale.russian',
  [LocaleEnum.en]: 'locale.english',
  [LocaleEnum.fr]: 'locale.french',
}

export const localeEnumOptions: OptionEnum<LocaleEnum>[] = Object.keys(
  LocaleEnum,
).map((value) => {
  const label = localeEnumLabels[value as keyof typeof LocaleEnum]
  return {
    label,
    id: value as LocaleEnum,
  }
})

export const InfoSourceEnumLabels: Record<InfoSourceEnum, string> = {
  [InfoSourceEnum.RECOMMENDATION]: 'info source.recommendation',
  [InfoSourceEnum.PREVIOUS_WORK]: 'info source.previous work',
  [InfoSourceEnum.AVITO]: 'info source.avito',
  [InfoSourceEnum.CAR_ADVERTISEMENT]: 'info source.car advertisement',
  [InfoSourceEnum.TELEGRAM]: 'info source.telegram',
  [InfoSourceEnum.LEAFLET]: 'info source.leaflet',
  [InfoSourceEnum.YANDEX_MAPS]: 'info source.yandex maps',
  [InfoSourceEnum.YANDEX_GARAGE]: 'info source.yandex garage',
  [InfoSourceEnum.BROWSER_ADVERTISEMENT]: 'info source.browser advertisement',
  [InfoSourceEnum.PASSED_BY]: 'info source.passed by',
  [InfoSourceEnum.SITE]: 'info source.site 369',
}

export const infoSourceOptions: OptionEnum<InfoSourceEnum>[] = Object.values(
  InfoSourceEnum,
).map((value) => ({
  label: InfoSourceEnumLabels[value as InfoSourceEnum],
  id: value as InfoSourceEnum,
}))

export const STATUS_VARIANT = {
  green: {
    backgroundColor: myPalette.green['100'],
    color: myPalette.green['600'],
  },
  outlinedGreen: {
    backgroundColor: myPalette.white.main,
    color: myPalette.green['600'],
    border: `1px solid ${myPalette.green['100']}`,
  },
  red: {
    backgroundColor: myPalette.red['100'],
    color: myPalette.red['600'],
  },
  outlinedRed: {
    backgroundColor: myPalette.white.main,
    color: myPalette.red['600'],
    border: `1px solid ${myPalette.red['600']}`,
  },
  gray: {
    backgroundColor: myPalette.gray['100'],
    color: myPalette.gray['700'],
  },
  yellow: {
    backgroundColor: myPalette.yellow['100'],
    color: myPalette.yellow['600'],
  },
  brand: {
    backgroundColor: myPalette.brand['100'],
    color: myPalette.brand['700'],
  },
  blue: {
    backgroundColor: myPalette.blue['100'],
    color: myPalette.blue['600'],
  },
  purple: {
    backgroundColor: myPalette.purple['100'],
    color: myPalette.purple['600'],
  },
}
